<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSubAccountsStore, useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, subAccountErrorKey } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import {
  LABEL_MAX_LENGTH,
  labelTooLong,
  relationshipStatus,
  type AccountRelationship,
  type AdminCreateSubAccountRequest,
  type AccountRelationshipType,
  type BillingMode,
  type TeamRole,
  type UpdateSubAccountRequest
} from '@/types/subaccount'
import type { AccountDetails, PaginatedResponse } from '@/types'

const props = defineProps<{
  accountId: number
}>()

const { t, d } = useI18n()
const subAccountsStore = useSubAccountsStore()
const notifyStore = useNotifyStore()

const loading = ref(true)
const loadError = ref(false)
const children = ref<AccountRelationship[]>([])
const parents = ref<AccountRelationship[]>([])
const showCreateDialog = ref(false)
const confirmDeleteId = ref<number | null>(null)

// Create form state
const createForm = ref({
  childAccountId: null as number | null,
  type: 'team' as AccountRelationshipType,
  billingMode: 'shared' as BillingMode,
  label: '',
  teamRole: 'viewer' as TeamRole
})

const editing = ref<AccountRelationship | null>(null)
const editForm = ref({
  billingMode: 'shared' as BillingMode,
  label: '',
  teamRole: 'viewer' as TeamRole
})
const saving = ref(false)

const teamRoleItems = () => [
  { title: t('dashboard.subAccounts.roles.viewer'), value: 'viewer' },
  { title: t('dashboard.subAccounts.roles.editor'), value: 'editor' },
  { title: t('dashboard.subAccounts.roles.manager'), value: 'manager' }
]

// Account search for create dialog
const accountQuery = ref('')
const allAccounts = ref<AccountDetails[]>([])
const loadingAccounts = ref(false)

const showDeleteDialog = computed({
  get: () => confirmDeleteId.value !== null,
  set: (val: boolean) => {
    if (!val) confirmDeleteId.value = null
  }
})

const filteredAccounts = computed(() => {
  if (!accountQuery.value.trim()) return allAccounts.value.slice(0, 20)
  const q = accountQuery.value.toLowerCase().trim()
  return allAccounts.value.filter(
    (acc) =>
      acc.email.toLowerCase().includes(q) ||
      (acc.name && acc.name.toLowerCase().includes(q)) ||
      (acc.surname && acc.surname.toLowerCase().includes(q)) ||
      (acc.org && acc.org.toLowerCase().includes(q))
  )
})

const formatDate = (dateStr: string): string => {
  try {
    return d(new Date(dateStr), 'short')
  } catch {
    return dateStr
  }
}

const getAccountDisplay = (rel: AccountRelationship, direction: 'child' | 'parent'): string => {
  const acc = direction === 'child' ? rel.child_account : rel.parent_account
  if (!acc) return rel.invite_email || '—'
  return acc.name || acc.email
}

const getAccountEmail = (rel: AccountRelationship, direction: 'child' | 'parent'): string => {
  const acc = direction === 'child' ? rel.child_account : rel.parent_account
  return acc?.email || rel.invite_email || ''
}

const getRelDisplayName = (rel: AccountRelationship): string => {
  return `${getAccountDisplay(rel, 'child')} (${rel.invite_email || getAccountEmail(rel, 'child')})`
}

const loadAccounts = async () => {
  loadingAccounts.value = true
  try {
    const res = await useAxios<null, PaginatedResponse<AccountDetails>>(
      'GET',
      '/account/all?limit=200',
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data?.data) {
      allAccounts.value = res.data.data as AccountDetails[]
    }
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'UserSubAccountsSection:loadAccounts' })
  } finally {
    loadingAccounts.value = false
  }
}

const notifyError = (error: unknown) => {
  notifyStore.createNotify({
    id: uuid('v4'),
    alertType: 'error',
    title: t('common.error'),
    message: t(subAccountErrorKey(error))
  })
}

const fetchData = async () => {
  loading.value = true
  loadError.value = false
  try {
    const result = await subAccountsStore.adminListSubAccounts(props.accountId)
    if (result) {
      children.value = result.children
      parents.value = result.parents
    } else {
      loadError.value = true
    }
  } catch {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

const openCreateDialog = () => {
  createForm.value = {
    childAccountId: null,
    type: 'team',
    billingMode: 'shared',
    label: '',
    teamRole: 'viewer'
  }
  accountQuery.value = ''
  showCreateDialog.value = true
  if (allAccounts.value.length === 0) loadAccounts()
}

const handleCreate = async () => {
  if (!createForm.value.childAccountId) return

  const req: AdminCreateSubAccountRequest = {
    child_account_id: createForm.value.childAccountId,
    type: createForm.value.type,
    billing_mode: createForm.value.billingMode,
    label: createForm.value.label || undefined,
    team_role: createForm.value.type === 'team' ? createForm.value.teamRole : undefined
  }

  try {
    const result = await subAccountsStore.adminCreateSubAccount(props.accountId, req)
    if (result) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.subAccounts.notifications.created'),
        message: t('dashboard.admin.subAccounts.notifications.createdMessage')
      })
      showCreateDialog.value = false
      await fetchData()
    }
  } catch (error) {
    notifyError(error)
  }
}

const openEditDialog = (rel: AccountRelationship) => {
  editing.value = rel
  editForm.value = {
    billingMode: rel.billing_mode,
    label: rel.label ?? '',
    teamRole: rel.team_role ?? 'viewer'
  }
}

const handleEdit = async () => {
  const rel = editing.value
  if (!rel) return
  const updates: UpdateSubAccountRequest = {}
  if (editForm.value.label !== (rel.label ?? '')) updates.label = editForm.value.label
  if (editForm.value.billingMode !== rel.billing_mode) updates.billing_mode = editForm.value.billingMode
  if (rel.type === 'team' && editForm.value.teamRole !== (rel.team_role ?? 'viewer')) updates.team_role = editForm.value.teamRole
  if (Object.keys(updates).length === 0) {
    editing.value = null
    return
  }
  saving.value = true
  try {
    if (await subAccountsStore.adminUpdateSubAccount(props.accountId, rel.id, updates)) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.subAccounts.notifications.updated'),
        message: t('dashboard.admin.subAccounts.notifications.updatedMessage')
      })
      editing.value = null
      await fetchData()
    }
  } catch (error) {
    notifyError(error)
  } finally {
    saving.value = false
  }
}

const handleDelete = async (relId: number) => {
  try {
    const success = await subAccountsStore.adminDeleteSubAccount(props.accountId, relId)
    if (success) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.subAccounts.notifications.deleted'),
        message: t('dashboard.admin.subAccounts.notifications.deletedMessage')
      })
      confirmDeleteId.value = null
      await fetchData()
    }
  } catch (error) {
    notifyError(error)
  }
}

const getStatusLabel = (rel: AccountRelationship): string =>
  t(`dashboard.admin.subAccounts.status.${relationshipStatus(rel)}`)

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div class="user-roles-section">
    <!-- Section Header -->
    <div class="section-header">
      <div class="header-content">
        <div class="header-icon subaccount-icon">
          <v-icon icon="mdi-account-group" size="20" />
        </div>
        <div class="header-text">
          <h3>{{ $t('dashboard.admin.subAccounts.title') }}</h3>
          <p>{{ $t('dashboard.admin.subAccounts.subtitle') }}</p>
        </div>
      </div>
      <button class="assign-btn" @click="openCreateDialog">
        <v-icon icon="mdi-plus" size="18" />
        <span>{{ $t('dashboard.admin.subAccounts.addRelationship') }}</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <div v-else-if="loadError" class="empty-state load-error">
      <v-icon icon="mdi-alert-circle-outline" size="32" />
      <span class="empty-title">{{ $t('dashboard.admin.subAccounts.loadError') }}</span>
      <button class="assign-btn" @click="fetchData">
        <v-icon icon="mdi-refresh" size="16" />
        <span>{{ $t('dashboard.admin.subAccounts.retry') }}</span>
      </button>
    </div>

    <template v-else>
      <!-- Children (this account is parent) -->
      <div v-if="children.length > 0" class="sub-section">
        <h4 class="sub-section-title">{{ $t('dashboard.admin.subAccounts.childrenTitle') }}</h4>
        <div class="roles-list">
          <div v-for="rel in children" :key="rel.id" class="role-card">
            <div class="role-info">
              <div class="role-name">
                <v-icon :icon="rel.type === 'team' ? 'mdi-account-multiple' : 'mdi-store'" size="18" />
                <span>{{ getAccountDisplay(rel, 'child') }}</span>
                <span class="type-badge" :class="rel.type">{{ rel.type }}</span>
                <span class="status-badge" :class="relationshipStatus(rel)">{{ getStatusLabel(rel) }}</span>
              </div>
              <div class="role-description">{{ getAccountEmail(rel, 'child') }}</div>
              <div class="role-meta">
                <span class="meta-item">
                  <v-icon icon="mdi-currency-usd" size="14" />
                  {{ rel.billing_mode === 'shared'
                    ? $t('dashboard.admin.subAccounts.billingShared')
                    : $t('dashboard.admin.subAccounts.billingIndependent')
                  }}
                </span>
                <span v-if="rel.label" class="meta-item">
                  <v-icon icon="mdi-label" size="14" />
                  {{ rel.label }}
                </span>
                <span v-if="rel.type === 'team' && rel.team_role" class="meta-item team-role">
                  <v-icon icon="mdi-account-key" size="14" />
                  {{ $t(`dashboard.subAccounts.roles.${rel.team_role}`) }}
                </span>
                <span class="meta-item">
                  <v-icon icon="mdi-calendar-check" size="14" />
                  {{ formatDate(rel.created_at) }}
                </span>
              </div>
            </div>
            <div class="role-actions">
              <button class="revoke-btn edit-btn" :title="$t('dashboard.admin.subAccounts.editRelationship')" @click="openEditDialog(rel)">
                <v-icon icon="mdi-pencil-outline" size="16" />
              </button>
              <v-dialog v-model="showDeleteDialog" max-width="400" persistent>
                <template #activator="{ props: dialogProps }">
                  <button
                    class="revoke-btn"
                    v-bind="dialogProps"
                    @click="confirmDeleteId = rel.id"
                  >
                    <v-icon icon="mdi-close" size="16" />
                  </button>
                </template>
                <div v-if="confirmDeleteId === rel.id" class="dialog-card">
                  <div class="dialog-header">
                    <div class="dialog-icon delete">
                      <v-icon icon="mdi-account-remove" size="24" />
                    </div>
                    <h3 class="dialog-title">{{ $t('dashboard.admin.subAccounts.deleteRelationship') }}</h3>
                    <p class="dialog-subtitle">
                      {{ $t('dashboard.admin.subAccounts.confirmDelete', { account: getRelDisplayName(rel) }) }}
                    </p>
                  </div>

                  <div class="dialog-actions">
                    <button class="dialog-btn secondary" @click="confirmDeleteId = null">
                      {{ $t('common.cancel') }}
                    </button>
                    <button class="dialog-btn danger" @click="handleDelete(rel.id)">
                      <v-icon icon="mdi-delete" size="16" />
                      <span>{{ $t('dashboard.admin.subAccounts.deleteRelationship') }}</span>
                    </button>
                  </div>
                </div>
              </v-dialog>
            </div>
          </div>
        </div>
      </div>

      <!-- Parents (this account is child) -->
      <div v-if="parents.length > 0" class="sub-section">
        <h4 class="sub-section-title">{{ $t('dashboard.admin.subAccounts.parentsTitle') }}</h4>
        <div class="roles-list">
          <div v-for="rel in parents" :key="rel.id" class="role-card">
            <div class="role-info">
              <div class="role-name">
                <v-icon :icon="rel.type === 'team' ? 'mdi-account-multiple' : 'mdi-store'" size="18" />
                <span>{{ getAccountDisplay(rel, 'parent') }}</span>
                <span class="type-badge" :class="rel.type">{{ rel.type }}</span>
                <span class="status-badge" :class="relationshipStatus(rel)">{{ getStatusLabel(rel) }}</span>
              </div>
              <div class="role-description">{{ getAccountEmail(rel, 'parent') }}</div>
              <div class="role-meta">
                <span class="meta-item">
                  <v-icon icon="mdi-currency-usd" size="14" />
                  {{ rel.billing_mode === 'shared'
                    ? $t('dashboard.admin.subAccounts.billingShared')
                    : $t('dashboard.admin.subAccounts.billingIndependent')
                  }}
                </span>
                <span class="meta-item">
                  <v-icon icon="mdi-calendar-check" size="14" />
                  {{ formatDate(rel.created_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="children.length === 0 && parents.length === 0" class="empty-state">
        <v-icon icon="mdi-account-group-outline" size="32" />
        <span class="empty-title">{{ $t('dashboard.admin.subAccounts.noRelationships') }}</span>
        <span class="empty-description">{{ $t('dashboard.admin.subAccounts.noRelationshipsDescription') }}</span>
      </div>
    </template>

    <!-- Create Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="440" persistent>
      <div class="dialog-card">
        <div class="dialog-header">
          <div class="dialog-icon create">
            <v-icon icon="mdi-account-plus" size="24" />
          </div>
          <h3 class="dialog-title">{{ $t('dashboard.admin.subAccounts.addRelationship') }}</h3>
        </div>

        <div class="dialog-body">
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.childAccount') }}</label>
            <v-autocomplete
              v-model="createForm.childAccountId"
              :items="filteredAccounts"
              :item-title="(acc: AccountDetails) => `${acc.email} (${acc.name || ''} ${acc.surname || ''})`"
              item-value="id"
              v-model:search="accountQuery"
              variant="outlined"
              density="compact"
              hide-details
              :loading="loadingAccounts"
              placeholder="Account suchen..."
            />
          </div>
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.type') }}</label>
            <v-select
              v-model="createForm.type"
              :items="[
                { title: $t('dashboard.admin.subAccounts.types.team'), value: 'team' },
                { title: $t('dashboard.admin.subAccounts.types.reseller'), value: 'reseller' }
              ]"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
          <div v-if="createForm.type === 'team'" class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.teamRole') }}</label>
            <v-select
              v-model="createForm.teamRole"
              class="create-team-role"
              :items="teamRoleItems()"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.billingMode') }}</label>
            <v-select
              v-model="createForm.billingMode"
              :items="[
                { title: $t('dashboard.admin.subAccounts.billing.shared'), value: 'shared' },
                { title: $t('dashboard.admin.subAccounts.billing.independent'), value: 'independent' }
              ]"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.label') }}</label>
            <input
              v-model="createForm.label"
              type="text"
              :maxlength="LABEL_MAX_LENGTH"
              class="text-input"
              placeholder="Optionale Bezeichnung..."
            />
            <p v-if="labelTooLong(createForm.label)" class="dialog-hint label-too-long">
              {{ $t('dashboard.subAccounts.errors.labelTooLong') }}
            </p>
          </div>
        </div>

        <div class="dialog-actions">
          <button class="dialog-btn secondary" @click="showCreateDialog = false">
            {{ $t('common.cancel') }}
          </button>
          <button
            class="dialog-btn primary"
            :disabled="!createForm.childAccountId || labelTooLong(createForm.label)"
            @click="handleCreate"
          >
            <v-icon icon="mdi-account-plus" size="16" />
            <span>{{ $t('dashboard.admin.subAccounts.addRelationship') }}</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog :model-value="!!editing" max-width="440" persistent @update:model-value="!$event && (editing = null)">
      <div v-if="editing" class="dialog-card edit-dialog">
        <div class="dialog-header">
          <div class="dialog-icon create">
            <v-icon icon="mdi-account-edit" size="24" />
          </div>
          <h3 class="dialog-title">{{ $t('dashboard.admin.subAccounts.editRelationship') }}</h3>
          <p class="dialog-subtitle">{{ getRelDisplayName(editing) }}</p>
        </div>

        <div class="dialog-body">
          <div v-if="editing.type === 'team'" class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.teamRole') }}</label>
            <v-select
              v-model="editForm.teamRole"
              class="edit-team-role"
              :items="teamRoleItems()"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.billingMode') }}</label>
            <v-select
              v-model="editForm.billingMode"
              :items="[
                { title: $t('dashboard.admin.subAccounts.billing.shared'), value: 'shared' },
                { title: $t('dashboard.admin.subAccounts.billing.independent'), value: 'independent' }
              ]"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
          <div class="dialog-field">
            <label class="dialog-label">{{ $t('dashboard.admin.subAccounts.form.label') }}</label>
            <input v-model="editForm.label" type="text" class="text-input edit-label" :maxlength="LABEL_MAX_LENGTH" />
            <p v-if="labelTooLong(editForm.label)" class="dialog-hint label-too-long">
              {{ $t('dashboard.subAccounts.errors.labelTooLong') }}
            </p>
          </div>
        </div>

        <div class="dialog-actions">
          <button class="dialog-btn secondary" :disabled="saving" @click="editing = null">
            {{ $t('common.cancel') }}
          </button>
          <button class="dialog-btn primary save-edit" :disabled="saving || labelTooLong(editForm.label)" @click="handleEdit">
            <v-icon icon="mdi-content-save" size="16" />
            <span>{{ $t('common.save') }}</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.user-roles-section {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}

.header-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;

  &.subaccount-icon {
    background: rgba(0, 150, 136, 0.12);
    border: 1px solid rgba(0, 150, 136, 0.25);
    color: #4db6ac;
  }
}

.header-text {
  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  p {
    margin: 2px 0 0;
    font-size: 0.8rem;
    color: rgba(200, 218, 255, 0.5);
  }
}

.assign-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(0, 150, 136, 0.1);
  border: 1px solid rgba(0, 150, 136, 0.3);
  border-radius: 8px;
  color: #4db6ac;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 150, 136, 0.2);
  }
}

.sub-section {
  &:not(:last-child) {
    margin-bottom: 16px;
  }
}

.sub-section-title {
  margin: 0 0 10px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(200, 218, 255, 0.4);
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  color: rgba(200, 218, 255, 0.4);

  .empty-title {
    font-weight: 500;
    color: rgba(200, 218, 255, 0.6);
  }

  .empty-description {
    font-size: 0.85rem;
  }
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.role-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba(0, 150, 136, 0.05);
  border: 1px solid rgba(0, 150, 136, 0.15);
  border-radius: 8px;
}

.role-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.role-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.type-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;

  &.team {
    background: rgba(33, 150, 243, 0.15);
    color: #42a5f5;
  }

  &.reseller {
    background: rgba(255, 152, 0, 0.15);
    color: #ffa726;
  }
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;

  &.active {
    background: rgba(76, 175, 80, 0.15);
    color: #66bb6a;
  }

  &.pending {
    background: rgba(255, 193, 7, 0.15);
    color: #ffd54f;
  }

  &.deactivated {
    background: rgba(244, 67, 54, 0.15);
    color: #e57373;
  }

  &.expired {
    background: rgba(158, 158, 158, 0.15);
    color: #bdbdbd;
  }
}

.role-description {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
  padding-left: 26px;
}

.role-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-left: 26px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: rgba(200, 218, 255, 0.5);
}

.revoke-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 6px;
  color: #ef5350;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.2);
    border-color: rgba(244, 67, 54, 0.3);
  }
}

.dialog-card {
  background: rgb(20, 24, 36);
  border: 1px solid rgba(120, 163, 255, 0.12);
  border-radius: 16px;
  padding: 28px 24px 20px;
}

.dialog-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  margin-bottom: 24px;
}

.dialog-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;

  &.create {
    background: rgba(0, 150, 136, 0.12);
    border: 1px solid rgba(0, 150, 136, 0.25);
    color: #4db6ac;
  }

  &.delete {
    background: rgba(244, 67, 54, 0.12);
    border: 1px solid rgba(244, 67, 54, 0.25);
    color: #ef5350;
  }
}

.dialog-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.dialog-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  line-height: 1.4;
}

.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.dialog-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dialog-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.text-input {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 150, 136, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.dialog-hint {
  margin: 6px 0 0;
  font-size: 0.75rem;
  color: #e57373;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.dialog-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: rgba(0, 150, 136, 0.15);
    border: 1px solid rgba(0, 150, 136, 0.35);
    color: #4db6ac;

    &:hover:not(:disabled) {
      background: rgba(0, 150, 136, 0.25);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &.danger {
    background: rgba(244, 67, 54, 0.15);
    border: 1px solid rgba(244, 67, 54, 0.35);
    color: #ef5350;

    &:hover {
      background: rgba(244, 67, 54, 0.25);
    }
  }

  &.secondary {
    background: transparent;
    border: 1px solid rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.6);

    &:hover {
      background: rgba(120, 163, 255, 0.05);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .user-roles-section {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .header-text h3 {
    color: rgba(0, 0, 0, 0.87);
  }

  .header-text p {
    color: rgba(0, 0, 0, 0.54);
  }

  .empty-state {
    color: rgba(0, 0, 0, 0.38);

    .empty-title {
      color: rgba(0, 0, 0, 0.6);
    }
  }

  .role-card {
    background: rgba(0, 150, 136, 0.04);
    border-color: rgba(0, 150, 136, 0.12);
  }

  .role-name {
    color: rgba(0, 0, 0, 0.87);
  }

  .role-description {
    color: rgba(0, 0, 0, 0.6);
  }

  .meta-item {
    color: rgba(0, 0, 0, 0.54);
  }

  .dialog-card {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.08);
  }

  .dialog-title {
    color: rgba(0, 0, 0, 0.87);
  }

  .dialog-subtitle {
    color: rgba(0, 0, 0, 0.6);
  }

  .dialog-label {
    color: rgba(0, 0, 0, 0.7);
  }

  .text-input {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.15);
    color: rgba(0, 0, 0, 0.87);
  }
}
</style>
